import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useTranslation } from "react-i18next";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { formatNumberToPrice, formatNumberToDkNumber, padNumber } from "utils/formats";
import { costumPalette } from "theme/palette";
import { GetProjectResponse, WorkItemResponse } from "api/generatedApi";
import { Typography2LinesStyled } from "shared/styles/typography-styled";
import { useWorkItemRestrictions } from "shared/user-restrictions/use-workitems-restrictions";
import { useDialog } from "shared/dialog/use-dialog";
import { ShowWorkItemInfoDialog } from "../../info/show-work-item-info-dialog";
import { TableCellStyled, TypographyStyled } from "./mobile-work-item-row-styles";
import { useEditWorkItemAmount } from "../../edit/edit-work-item-amount/use-edit-work-item-amount";
import { useWorkitem } from "../../hooks/use-workitem";
import { ItemDot } from "components/shared/item-dot/item-dot";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

interface Prop {
  project: GetProjectResponse;
  folder: ExtendedProjectFolder;
  workItem: WorkItemResponse;
  onCheck: (id: string | undefined) => void;
  checked: boolean;
  shouldScrollIntoView?: boolean;
  setLastEdited: Dispatch<SetStateAction<string | null>>;
  scrollToRow: (rowRef: React.RefObject<HTMLTableRowElement>, shouldScrollIntoView: boolean) => void;
}

export const MobileWorkItemMaterialTableRow = (props: Prop) => {
  const { project, folder, workItem, onCheck, checked, shouldScrollIntoView, setLastEdited } = props;
  const { t } = useTranslation();
  const { canSelectWorkItem, canEditWorkitem } = useWorkItemRestrictions(project);
  const [openInfoDialog] = useDialog(ShowWorkItemInfoDialog);
  const openEditAmountDialog = useEditWorkItemAmount({ project, folder, workItem, setLastEdited });
  const { workItemIsRecognised, workItemTitle } = useWorkitem({ workItem });

  const showWorkItemInfo = () => {
    openInfoDialog({ project, folder, workItem, setLastEdited });
    setLastEdited(workItem.workItemId ?? null);
  };

  const rowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    props.scrollToRow(rowRef, shouldScrollIntoView ?? false);
  }, [shouldScrollIntoView, props.scrollToRow]);

  return (
    <TableRow sx={{ cursor: "pointer" }} ref={rowRef}>
      <TableCell sx={{ p: 0, pt: 0.5, verticalAlign: "top" }}>
        <Checkbox data-testid="mobile-measurement-table-material-row-checkbox" onChange={(event) => onCheck(workItem.workItemId)} onClick={(event) => event.stopPropagation()} checked={checked} disabled={!canSelectWorkItem(folder)} />
      </TableCell>
      <TableCellStyled title={workItemTitle} sx={{ pr: 1, pt: 2, verticalAlign: "top" }} onClick={showWorkItemInfo}>
        <Box>
          <Typography2LinesStyled variant="body2" color={"primary.main"}>
            {!workItemIsRecognised && <ItemDot sx={{ marginRight: "5px" }} />}
            {workItem.workItemText}
          </Typography2LinesStyled>
          {workItem.workItemMaterial?.workItemMountingCode && workItem.workItemMaterial?.workItemMountingCode > 0 && (
            <TypographyStyled variant="body2" color={"grey.50"}>
              {padNumber(workItem.workItemMaterial?.workItemMountingCode)} - {workItem.workItemMaterial?.workItemMountingCodeText}
            </TypographyStyled>
          )}
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {workItem.workItemMaterial?.supplementOperations && workItem.workItemMaterial?.supplementOperations.length > 0 && (
              <Chip size="small" label={t("content.measurements.table.cellValue.operationSupplementSmall")} sx={{ bgcolor: costumPalette.operationSupplementBg, color: costumPalette.operationSupplementColor, marginRight: 0.5 }} />
            )}
            {workItem.supplements && workItem.supplements.length > 0 && (
              <Chip size="small" label={t("content.measurements.table.cellValue.supplementSmall")} sx={{ bgcolor: costumPalette.supplementBg, color: costumPalette.supplementColor, marginRight: 0.5 }} />
            )}
          </Box>
        </Box>
      </TableCellStyled>
      <TableCell sx={{ pl: 0, pr: 0, textAlign: "center", verticalAlign: "top" }} onClick={canEditWorkitem(folder) ? openEditAmountDialog : showWorkItemInfo}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body2" color={"primary.main"}>
            {formatNumberToDkNumber(workItem.workItemAmount)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ p: 0, textAlign: "right", pt: 2, pr: 1, verticalAlign: "top", color: "primary.main" }} onClick={showWorkItemInfo}>
        {formatNumberToPrice(workItem.workItemTotalPaymentDkr)}
      </TableCell>
    </TableRow>
  );
};
